import React, { useState } from "react";
import {
  Check,
  Edit,
  GripVertical,
  MoreVertical,
  Trash,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CannedResponse } from "@/types/cannedResponse";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { TemplateTextArea } from "@/components/pages/faqs/TemplateTextArea";
import {
  useDeleteCannedResponse,
  useUpdateCannedResponse,
} from "./hooks/canned-responses.hooks";
import { cn } from "@/lib/utils";

interface CannedResponseItemProps {
  response: CannedResponse;
}

export const CannedResponseItem: React.FC<CannedResponseItemProps> = ({
  response,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editData, setEditData] = useState({
    text: response.text || "",
  });

  const deleteCannedResponse = useDeleteCannedResponse();
  const updateCannedResponse = useUpdateCannedResponse();
  const normalizedText = editData.text.trim();

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id: response.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: "none",
    opacity: isDragging ? 0.5 : 1,
  };

  const handleStartEdit = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    setEditData({ text: response.text || "" });
    setIsEditing(true);
  };

  const handleSave = (event?: React.MouseEvent): void => {
    event?.stopPropagation();
    if (!response.id || !normalizedText) return;

    updateCannedResponse.mutate(
      {
        id: response.id,
        data: { text: normalizedText },
      },
      {
        onSuccess: () => setIsEditing(false),
      },
    );
  };

  const handleCancel = (event?: React.MouseEvent): void => {
    event?.stopPropagation();
    setEditData({ text: response.text || "" });
    setIsEditing(false);
  };

  const handleDelete = (event?: React.MouseEvent): void => {
    event?.stopPropagation();
    setDeleteOpen(true);
  };

  const handleConfirmDelete = (): void => {
    if (!response.id) return;

    deleteCannedResponse.mutate(response.id, {
      onSuccess: () => setDeleteOpen(false),
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          "border-base-border flex min-h-12 items-center gap-3 rounded-lg border bg-white px-3 py-2 transition-colors",
          isEditing && "border-custom-outline bg-secondary-badge",
        )}
      >
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab text-black/45 transition-colors hover:text-black/96"
          onClick={(event) => event.stopPropagation()}
        >
          <GripVertical className="size-4" />
        </div>

        <span className="line-clamp-2 min-w-0 flex-1 text-sm font-medium whitespace-normal text-black/96">
          {response.text}
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger
            asChild
            onClick={(event) => event.stopPropagation()}
          >
            <Button
              variant="new_ghost"
              size="icon_sm"
              aria-label={t("chats.cannedResponsesManagement.actions")}
            >
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={handleStartEdit}>
                <Edit />
                <span>{t("chats.cannedResponsesManagement.edit")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete} variant="destructive">
                <Trash />
                <span>{t("chats.cannedResponsesManagement.delete")}</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isEditing ? (
        <div className="border-base-border bg-secondary-badge flex flex-col gap-4 rounded-xl border p-4">
          <h3 className="text-sm font-semibold text-black/96">
            {t("chats.cannedResponsesManagement.edit")}
          </h3>
          <TemplateTextArea
            label={t("chats.cannedResponsesManagement.responseLabel")}
            placeholder={t(
              "chats.cannedResponsesManagement.responsePlaceholder",
            )}
            value={editData.text}
            onChange={(text) => setEditData({ text })}
            actions={
              <div className="flex justify-end gap-2">
                <Button variant="new_outline" onClick={handleCancel}>
                  <X /> {t("common.cancel")}
                </Button>
                <Button
                  variant="new_default"
                  onClick={handleSave}
                  disabled={!normalizedText || updateCannedResponse.isPending}
                >
                  <Check /> {t("common.save")}
                </Button>
              </div>
            }
          />
        </div>
      ) : null}

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogOverlay />
        <DialogContent
          showCloseButton={false}
          className="border-base-border w-[425px] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden rounded-xl border bg-white p-0 shadow-lg sm:max-w-[425px]"
          onClick={(event) => event.stopPropagation()}
        >
          <DialogHeader className="border-base-border gap-2 border-b px-6 py-4 text-left">
            <DialogTitle className="text-base leading-normal font-semibold text-black/96">
              {t("chats.cannedResponsesManagement.deleteTitle")}
            </DialogTitle>
            <DialogDescription className="text-sm leading-normal text-black/65">
              {t("chats.cannedResponsesManagement.deleteDescription")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="border-base-border flex-row justify-end gap-2 border-t px-6 py-4">
            <DialogClose asChild>
              <Button
                variant="new_outline"
                disabled={deleteCannedResponse.isPending}
              >
                {t("common.cancel")}
              </Button>
            </DialogClose>
            <Button
              variant="new_destructive"
              onClick={handleConfirmDelete}
              disabled={deleteCannedResponse.isPending}
            >
              {t("chats.cannedResponsesManagement.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
